/* eslint-disable react/prop-types */
import { Bell, EmoNeutral } from "@assets"
import { Head, Link, router, usePage } from "@inertiajs/react"
import type React from "react"
import { useEffect } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { feedPath, forumPath } from "@/routes"
import type { SharedData } from "@/types"

interface NotificationItem {
  id: number | string
  title?: string
  body?: string | null
  url?: string | null
  is_read?: boolean
  created_at?: string
}

interface PageProps {
  notifications: NotificationItem[]
  filters?: { unread?: string; type?: string }
  pagination?: {
    current_offset?: number
    limit?: number
    total?: number
    has_more?: boolean
    current_page?: number
    total_pages?: number
  }
  unread_count?: number
}

const toRelativePath = (url: string): string => {
  try {
    const parsed = new URL(url)
    return parsed.pathname + parsed.search + parsed.hash
  } catch {
    return url
  }
}

export default function NotificationsIndex() {
  const { props } = usePage<SharedData & PageProps>()
  const { notifications = [], unread_count = 0 } = props

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("notifications:updated", { detail: { unread_count: 0 } }),
    )
  }, [])

  const csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute("content")
  }

  const markAsRead = async (id: number | string) => {
    try {
      const res = await fetch(`/notificaciones/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken() ?? "",
        },
        body: JSON.stringify({ action_type: "mark_as_read" }),
        credentials: "same-origin",
      })
      const resData = (await res.json()) as { unread_count?: number }

      if (typeof resData.unread_count === "number") {
        window.dispatchEvent(
          new CustomEvent("notifications:updated", {
            detail: { unread_count: resData.unread_count },
          }),
        )
      }

      router.reload({ only: ["notifications", "unread_count", "auth"] })
    } catch {
      // no-op
    }
  }

  const handleMarkAsRead = (id: number | string) => {
    void markAsRead(id)
  }

  const handleTitleClick = async (
    e: React.MouseEvent,
    id: number | string,
    url: string | null | undefined,
    isRead: boolean,
  ) => {
    e.preventDefault()
    if (!isRead) {
      try {
        const res = await fetch(`/notificaciones/${id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken() ?? "",
          },
          body: JSON.stringify({ action_type: "mark_as_read" }),
          credentials: "same-origin",
        })
        const resData = (await res.json()) as { unread_count?: number }

        if (typeof resData.unread_count === "number") {
          window.dispatchEvent(
            new CustomEvent("notifications:updated", {
              detail: { unread_count: resData.unread_count },
            }),
          )
        }
      } catch {
        // no-op
      }
    }
    if (url) {
      router.flushAll()
      router.visit(toRelativePath(url))
    }
  }

  return (
    <AppLayout>
      <Head title="Notificaciones" />

      <div className="mx-auto max-w-3xl space-y-6 py-4 md:p-6">
        <div className="flex items-center justify-center">
          <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
            <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
              <Bell className="text-primary h-8 w-8" />
            </span>
            Notificaciones
          </h1>
          {unread_count > 0 && (
            <Badge variant="secondary" className="text-xs">
              {unread_count} sin leer
            </Badge>
          )}
        </div>

        {notifications.length === 0 ? (
          <div
            className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
            style={{ background: "var(--gradient-highlight)" }}
          >
            <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
              <img src={EmoNeutral} alt="Sin notificaciones" />
            </div>

            <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
              Aún no tienes notificaciones
            </h3>
            <p className="text-muted-foreground mt-2 text-sm text-balance">
              Participa en el foro o explora el feed para empezar a ver
              actividad aquí.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Link href={forumPath()}>
                <Button size="sm">Ir al Foro</Button>
              </Link>
              <Link href={feedPath()}>
                <Button size="sm" variant="ghost">
                  Ver Feed
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <ul className=" ">
            {notifications.map((n) => (
              <li
                key={n.id}
                className="border-input flex items-start gap-3 border-b p-4"
              >
                <div className="flex-1">
                  {n.url ? (
                    <a
                      href={n.url}
                      className="font-medium hover:underline"
                      onClick={(e) => {
                        void handleTitleClick(
                          e,
                          n.id,
                          n.url,
                          n.is_read ?? false,
                        )
                      }}
                    >
                      {n.title ?? "Notificación"}
                    </a>
                  ) : (
                    <span className="font-medium">
                      {n.title ?? "Notificación"}
                    </span>
                  )}
                  {n.body && (
                    <p className="text-muted-foreground mt-1 text-sm">
                      {n.body}
                    </p>
                  )}
                  {n.created_at && (
                    <p className="text-muted-foreground mt-2 text-xs">
                      {n.created_at}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {n.is_read ? (
                    <Badge variant="outline" className="text-xs">
                      Leída
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleMarkAsRead(n.id)}
                    >
                      Marcar como leída
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppLayout>
  )
}
